import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, X, Trash2 } from 'lucide-react';
import type { Photo } from '../types/api';

interface GalleryImage {
  url: string;
  source: string;
  photoId?: string;
}

interface PhotoGalleryProps {
  listingImages: string[];
  photos: Photo[];
  onRemovePhoto?: (photoId: string) => void;
}

function collectImages(listingImages: string[], photos: Photo[]): GalleryImage[] {
  const images: GalleryImage[] = [];

  for (const url of listingImages) {
    images.push({ url, source: 'Listing' });
  }

  for (const photo of photos) {
    images.push({ url: photo.url, source: photo.addedBy, photoId: photo.id });
  }

  return images;
}

function getTouchDistance(touches: React.TouchList): number {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

export function PhotoGallery({ listingImages, photos, onRemovePhoto }: PhotoGalleryProps) {
  const { t } = useTranslation();
  const images = collectImages(listingImages, photos);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isGesturing, setIsGesturing] = useState(false);

  const touchStartX = useRef<number | null>(null);
  const pinchStartDist = useRef<number | null>(null);
  const pinchStartScale = useRef(1);
  const lastTapTime = useRef(0);
  const panStart = useRef<{ x: number; y: number } | null>(null);
  const panTranslateStart = useRef({ x: 0, y: 0 });
  const lightboxRef = useRef<HTMLDivElement>(null);

  const resetZoom = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIndex(null);
    resetZoom();
  }, [resetZoom]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) => (i !== null ? (i + 1) % images.length : null));
    resetZoom();
  }, [images.length, resetZoom]);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) => (i !== null ? (i - 1 + images.length) % images.length : null));
    resetZoom();
  }, [images.length, resetZoom]);

  useEffect(() => {
    if (lightboxIndex === null) return;

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.3 : 0.3;
      setScale((prev) => {
        const next = Math.min(4, Math.max(1, prev + delta));
        if (next <= 1) setTranslate({ x: 0, y: 0 });
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, goNext, goPrev]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      pinchStartDist.current = getTouchDistance(e.touches);
      pinchStartScale.current = scale;
      touchStartX.current = null;
      panStart.current = null;
      setIsGesturing(true);
    } else if (e.touches.length === 1) {
      const now = Date.now();
      const timeSinceLastTap = now - lastTapTime.current;
      lastTapTime.current = now;

      if (timeSinceLastTap < 300) {
        if (scale > 1) {
          resetZoom();
        } else {
          setScale(2);
          setTranslate({ x: 0, y: 0 });
        }
        touchStartX.current = null;
        panStart.current = null;
        lastTapTime.current = 0;
        return;
      }

      if (scale > 1) {
        panStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        panTranslateStart.current = { ...translate };
        touchStartX.current = null;
      } else {
        touchStartX.current = e.touches[0].clientX;
        panStart.current = null;
      }
    }
  }, [scale, translate, resetZoom]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchStartDist.current !== null) {
      const dist = getTouchDistance(e.touches);
      const newScale = Math.min(4, Math.max(1, pinchStartScale.current * (dist / pinchStartDist.current)));
      setScale(newScale);
      if (newScale <= 1) setTranslate({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && panStart.current !== null && scale > 1) {
      setIsGesturing(true);
      const dx = e.touches[0].clientX - panStart.current.x;
      const dy = e.touches[0].clientY - panStart.current.y;
      setTranslate({
        x: panTranslateStart.current.x + dx,
        y: panTranslateStart.current.y + dy,
      });
    }
  }, [scale]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (pinchStartDist.current !== null) {
      pinchStartDist.current = null;
      setIsGesturing(false);
      setScale((s) => {
        if (s < 1.1) {
          setTranslate({ x: 0, y: 0 });
          return 1;
        }
        return s;
      });
      return;
    }

    panStart.current = null;
    setIsGesturing(false);

    if (touchStartX.current !== null && scale <= 1) {
      const diff = e.changedTouches[0].clientX - touchStartX.current;
      touchStartX.current = null;
      if (Math.abs(diff) < 50) return;
      if (diff < 0) goNext();
      else goPrev();
      return;
    }
    touchStartX.current = null;
  }, [scale, goNext, goPrev]);

  if (images.length === 0) return null;

  const imageTransform = scale !== 1 || translate.x !== 0 || translate.y !== 0
    ? `translate(${translate.x}px, ${translate.y}px) scale(${scale})`
    : undefined;

  const currentImage = lightboxIndex !== null ? images[lightboxIndex] : null;

  return (
    <>
      <div className="detail-section">
        <label className="input-label">{t('app.photoGallery.photosLabel', { count: images.length })}</label>
        <div className="photo-gallery-grid">
          {images.map((img, i) => (
            <button
              key={i}
              className="photo-gallery-thumb"
              onClick={() => setLightboxIndex(i)}
              aria-label={t('app.photoGallery.viewPhotoLabel', { index: i + 1, source: img.source })}
            >
              <img src={img.url} alt={`Photo ${i + 1}`} loading="lazy" />
              <span className="photo-source">{img.source}</span>
              {img.photoId && onRemovePhoto && (
                <button
                  className="photo-delete-btn"
                  onClick={(e) => { e.stopPropagation(); onRemovePhoto(img.photoId!); }}
                  aria-label={t('app.photoGallery.deletePhoto')}
                >
                  <Trash2 size={12} />
                </button>
              )}
            </button>
          ))}
        </div>
      </div>

      {lightboxIndex !== null && currentImage && (
        <div
          className="photo-lightbox"
          ref={lightboxRef}
          onClick={scale <= 1 ? closeLightbox : undefined}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button className="lightbox-close" onClick={closeLightbox} aria-label={t('common.close')}>
            <X size={24} />
          </button>
          {images.length > 1 && scale <= 1 && (
            <>
              <button className="lightbox-nav lightbox-prev" onClick={(e) => { e.stopPropagation(); goPrev(); }} aria-label={t('common.previous')}>
                <ChevronLeft size={32} />
              </button>
              <button className="lightbox-nav lightbox-next" onClick={(e) => { e.stopPropagation(); goNext(); }} aria-label={t('common.next')}>
                <ChevronRight size={32} />
              </button>
            </>
          )}
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={currentImage.url}
              alt={`Photo ${lightboxIndex + 1}`}
              draggable={false}
              style={imageTransform ? {
                transform: imageTransform,
                transition: isGesturing ? 'none' : 'transform 0.2s ease',
              } : undefined}
            />
            <div className="lightbox-caption">
              {t('app.photoGallery.caption', { index: lightboxIndex + 1, total: images.length, source: currentImage.source })}
              {scale > 1 && ` · ${Math.round(scale * 100)}%`}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
